import { createSlice, isAnyOf } from "@reduxjs/toolkit";
import { ConditionField, ReportType } from "../../../shared/reporting/api/biClient.types";
import { areAllFieldsEmpty, insertItemAt } from "../../../shared/reporting/utils/Utilities";
import {
  ensureDefaultFormatIsSet,
  getAggregationBasedFields,
  isAggregationBased,
  isMeasure,
  updateLinkAndValidate,
  updateTableField,
  updateTableFieldsOrder,
} from "../../../shared/reporting/utils/fieldsHelper";
import { getDimensionNames } from "../../../shared/reporting/utils/getDimensionNames";
import { getMeasureNames } from "../../../shared/reporting/utils/getMeasureNames";
import { createTileDefaultSettings } from "../../../shared/reporting/utils/isConfigurationValid";
import {
  calculateAndAssignSystemLabel,
  moveConditionItem,
  moveItem,
  removeConditionFromLinked,
  removeConditionItem,
  removeItem,
  updateConditionConfig,
  updateConditionItem,
} from "../../components/builder/common/utilities/fieldsState";
import { createTileReportConfiguration } from "../../components/builder/utils/configuration/tileConfiguration";
import { fieldsStateActions } from "../thunks/fieldStatesThunks";
import { TileFieldsActionTypes, TileFieldState } from "../types/fieldsState.types";
import { TILE_SLICE_NAME } from "../utils/storeUtils";

const initialState: TileFieldState = {
  conditions: [],
  fields: [],
  settings: createTileDefaultSettings(),
  dimensionNames: [],
  measureNames: [],
  configuration: undefined,
};

const tileFieldsSlice = createSlice({
  name: TILE_SLICE_NAME,
  initialState,
  reducers: {
    resetToDefaultAction: () => initialState,
    // CONDITIONS
    setConditions: (state, action: TileFieldsActionTypes["setConditions"]) => {
      state.conditions = action.payload;
    },
    addCondition: (state, action: TileFieldsActionTypes["addCondition"]) => {
      const condition: ConditionField = { ...action.payload.field };
      calculateAndAssignSystemLabel(condition, state.conditions);
      const conditions = insertItemAt(state.conditions, condition, action.payload.index);
      state.conditions = conditions;
    },
    removeCondition: (state, action: TileFieldsActionTypes["removeCondition"]) => {
      const conditions = removeConditionItem(action.payload, state.conditions);
      removeConditionFromLinked(action.payload.config.guid, getAggregationBasedFields(state.fields));
      state.conditions = conditions;
    },
    moveCondition: (state, action: TileFieldsActionTypes["moveCondition"]) => {
      state.conditions = moveConditionItem(action.payload.field, action.payload.newIndex, state.conditions);
    },
    updateCondition: (state, action: TileFieldsActionTypes["updateCondition"]) => {
      state.conditions = updateConditionItem(action.payload.field, action.payload.changes, state.conditions);
    },
    updateConditionConfig: (state, action: TileFieldsActionTypes["updateConditionConfig"]) => {
      const condition = state.conditions.find((c) => c.config.guid === action.payload.field.config.guid);
      if (condition) {
        condition.config = updateConditionConfig(condition.config, action.payload.changes);
      }
    },
    // FIELDS
    setFields: (state, action: TileFieldsActionTypes["setFields"]) => {
      state.fields = action.payload;
    },
    addField: (state, action: TileFieldsActionTypes["addField"]) => {
      state.fields = insertItemAt(state.fields, ensureDefaultFormatIsSet(action.payload.field), action.payload.index);
    },
    removeField: (state, action: TileFieldsActionTypes["removeField"]) => {
      const fields = removeItem(action.payload, state.fields);
      state.fields = fields;
      if (isMeasure(action.payload)) {
        state.conditions = updateLinkAndValidate(state.conditions, getAggregationBasedFields(fields));
      }
    },
    moveField: (state, action: TileFieldsActionTypes["moveField"]) => {
      state.fields = moveItem(action.payload.field, action.payload.newIndex, state.fields);
    },
    updateFieldConfig: (state, action: TileFieldsActionTypes["updateFieldConfig"]) => {
      const field = state.fields.find((f) => f.config.guid === action.payload.field.config.guid);
      if (field !== undefined) {
        field.config = { ...field.config, ...action.payload.changes };
        if (isAggregationBased(field)) {
          state.conditions = updateLinkAndValidate(state.conditions, getAggregationBasedFields(state.fields));
        }
      }
    },
    updateMeasureField: (state, action: TileFieldsActionTypes["updateMeasureField"]) => {
      state.fields = updateTableField(action.payload.field, action.payload.changes, state.fields);
    },
    updateFieldsOrder: (state, action: TileFieldsActionTypes["updateFieldsOrder"]) => {
      state.fields = updateTableFieldsOrder(state.fields, action.payload);
    },
    //SETTINGS
    updateSettings: (state, action: TileFieldsActionTypes["updateSettings"]) => {
      state.settings = { ...state.settings, ...action.payload };
    },
    setSettings: (state, action: TileFieldsActionTypes["setSettings"]) => {
      state.settings = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      isAnyOf(
        fieldsStateActions.setFields.fulfilled,
        fieldsStateActions.updateMeasureField.fulfilled,
        tileFieldsSlice.actions.setConditions,
        tileFieldsSlice.actions.addCondition,
        tileFieldsSlice.actions.removeCondition,
        tileFieldsSlice.actions.moveCondition,
        tileFieldsSlice.actions.updateMeasureField
      ),
      (state) => {
        state.conditions = updateLinkAndValidate(state.conditions, getAggregationBasedFields(state.fields));
      }
    );
    //This has to be the last matcher
    builder.addMatcher(isAnyOf(...Object.values(tileFieldsSlice.actions)), (state) => {
      const configuration = createTileReportConfiguration(state.conditions, state.fields, state.settings);
      state.configuration = configuration;
      const { settings, ...fields } = configuration;
      state.isConfigurationEmpty = areAllFieldsEmpty(fields);
      state.dimensionNames = getDimensionNames(ReportType.Tile, configuration);
      state.measureNames = getMeasureNames(ReportType.Tile, configuration);
    });
  },
});

export const tileFieldsActions = tileFieldsSlice.actions;
export const tileFieldsReducer = tileFieldsSlice.reducer;
